/**
 * Notifications service wrapper
 *
 * Overrides the generated types to correctly type the list response,
 * which returns a single object with notifications array and unread_count,
 * not an array of NotificationListResponseSchema.
 */
import { baseApiClient as api } from './baseService';
import type {
  CreateNotificationSchema as GeneratedCreateNotificationSchema,
  MarkReadSchema,
  NotificationSchema as GeneratedNotificationSchema,
} from './generated/notificationsService';

export const addTagTypes = ['notifications'] as const;

// Extended NotificationCategory to include MENTION
export type NotificationCategory =
  | 'SUGGESTION'
  | 'ASSIGNMENT'
  | 'ALERT'
  | 'REMINDER'
  | 'UPDATE'
  | 'INFO'
  | 'MENTION';

// Tab types for filtering
export type NotificationTab = 'all' | 'messages' | 'mentions' | 'updates';

// Unread counts by category
export interface UnreadCountsSchema {
  total: number;
  messages: number;
  mentions: number;
  updates: number;
}

// Re-export NotificationSchema with extended category type
export type NotificationSchema = Omit<
  GeneratedNotificationSchema,
  'category'
> & {
  category?: NotificationCategory;
};

// Re-export CreateNotificationSchema with extended category type
export type CreateNotificationSchema = Omit<
  GeneratedCreateNotificationSchema,
  'category'
> & {
  category?: NotificationCategory;
};

// Correct response type for the notifications list endpoint
export interface NotificationListResponse {
  notifications: NotificationSchema[];
  unread_count: number;
  unread_counts?: UnreadCountsSchema | null;
}

// --- Types for Notification Context Endpoint ---

export interface UserSummary {
  id: string;
  display_name: string;
  initials: string;
}

export interface MatterSummary {
  id: string;
  name: string | null;
  incident_type: string | null;
  incident_date: string | null;
  firm_name: string;
  lead_attorney: string | null;
  status: string | null;
}

export interface TaskUpdateSummary {
  id: string;
  author: UserSummary | null;
  content: string;
  created_at: string;
}

export interface TaskContext {
  id: string;
  title: string | null;
  assigned_to: string | null;
  due_date: string | null;
  status: string | null;
  priority: string | null;
  updates: TaskUpdateSummary[];
}

export interface NoteContext {
  id: string;
  title: string;
  content: string;
  author: UserSummary | null;
  created_at: string;
}

export interface NotificationContextResponse {
  notification: NotificationSchema;
  matter: MatterSummary | null;
  task: TaskContext | null;
  note: NoteContext | null;
  /** All notes on the matter for displaying note conversation thread */
  matter_notes: NoteContext[] | null;
}

// --- Client Conversations (SMS inbox for case managers) ---

export interface ClientConversation {
  matter_id: string;
  matter_name: string | null;
  client_name: string | null;
  latest_message_id: string;
  latest_message_preview: string;
  latest_message_timestamp: string;
  latest_message_is_inbound: boolean;
  unread_count: number;
  total_count: number;
}

export interface ClientConversationsListResponse {
  conversations: ClientConversation[];
  total_unread_count: number;
  has_more: boolean;
}

export interface ClientConversationsListApiArg {
  /** Search by message content, client name, or matter name */
  search?: string;
  /** If true, only return conversations where the latest message is from the client (unreplied) */
  unreplied?: boolean;
  /** Maximum number of conversations to return (default: 50) */
  limit?: number;
}

export interface ClientMessageRelatedMatter {
  matter_id: string;
  matter_name: string;
}

export interface ClientMessage {
  id: string;
  content: string;
  is_inbound: boolean;
  timestamp: string;
  from_number: string;
  to_numbers: string[];
  cross_matter_group_id?: string | null;
  related_matter_count?: number;
  related_matters?: ClientMessageRelatedMatter[];
}

export interface ClientConversationDetailResponse {
  matter_id: string;
  matter_name: string | null;
  client_name: string | null;
  matter: MatterSummary | null;
  messages: ClientMessage[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface ClientConversationDetailApiArg {
  matterId: string;
  /** Maximum number of messages to return (default: 100) */
  limit?: number;
  /** ISO timestamp cursor for pagination (older messages) */
  cursor?: string;
}

// Extended query args with new parameters
export interface NotificationListApiArg {
  /** Filter by tab: 'mentions', 'updates', or 'all' (default) */
  tab?: NotificationTab;
  /** Search by title or description (case-insensitive) */
  search?: string;
  /** Include unread counts by category in response */
  includeCounts?: boolean;
  /** Maximum number of notifications to return (default: 50) */
  limit?: number;
  /** Maximum number of read notifications to return (unlimited unread) */
  maxReadNotifications?: number;
  /** ISO timestamp - only return notifications created after this time */
  since?: string;
  /** If true, only return unread notifications (default: false) */
  unreadOnly?: boolean;
}

// Mark all read args with tab filter
export interface NotificationMarkAllReadApiArg {
  /** Filter by tab: 'mentions', 'updates', or 'all' (default) */
  tab?: NotificationTab;
}

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      notificationList: build.query<
        NotificationListResponse,
        NotificationListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/notifications/`,
          params: {
            tab: queryArg.tab,
            search: queryArg.search,
            include_counts: queryArg.includeCounts,
            limit: queryArg.limit,
            max_read_notifications: queryArg.maxReadNotifications,
            since: queryArg.since,
            unread_only: queryArg.unreadOnly,
          },
        }),
        extraOptions: { skipSentryOnStatus: [403] },
        providesTags: ['notifications'],
      }),
      notificationCreate: build.mutation<
        NotificationSchema,
        { createNotificationSchema: CreateNotificationSchema }
      >({
        query: (queryArg) => ({
          url: `/api/notifications/`,
          method: 'POST',
          body: queryArg.createNotificationSchema,
        }),
        invalidatesTags: ['notifications'],
      }),
      notificationMarkRead: build.mutation<
        void,
        { id: string; markReadSchema: MarkReadSchema }
      >({
        query: (queryArg) => ({
          url: `/api/notifications/${queryArg.id}/mark-read/`,
          method: 'POST',
          body: queryArg.markReadSchema,
        }),
        invalidatesTags: ['notifications'],
      }),
      notificationMarkAllRead: build.mutation<
        { updated_count: number },
        NotificationMarkAllReadApiArg | void
      >({
        query: (queryArg) => ({
          url: `/api/notifications/mark-all-read/`,
          method: 'POST',
          params: queryArg ? { tab: queryArg.tab } : undefined,
        }),
        invalidatesTags: ['notifications'],
      }),
      notificationUnreadCount: build.query<{ unread_count: number }, void>({
        query: () => ({ url: `/api/notifications/unread-count/` }),
        extraOptions: { skipSentryOnStatus: [403] },
        providesTags: ['notifications'],
      }),
      notificationUnreadCounts: build.query<UnreadCountsSchema, void>({
        query: () => ({ url: `/api/notifications/unread-counts/` }),
        extraOptions: { skipSentryOnStatus: [403] },
        providesTags: ['notifications'],
      }),
      notificationContext: build.query<
        NotificationContextResponse,
        { id: string }
      >({
        query: ({ id }) => ({ url: `/api/notifications/${id}/context/` }),
        providesTags: (result, error, { id }) => [{ type: 'notifications', id }],
      }),
      clientConversationsList: build.query<
        ClientConversationsListResponse,
        ClientConversationsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/notifications/client-messages/`,
          params: {
            search: queryArg.search,
            unreplied: queryArg.unreplied,
            limit: queryArg.limit,
          },
        }),
        providesTags: ['notifications'],
      }),
      clientConversationDetail: build.query<
        ClientConversationDetailResponse,
        ClientConversationDetailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/notifications/client-messages/${queryArg.matterId}/`,
          params: {
            limit: queryArg.limit,
            cursor: queryArg.cursor,
          },
        }),
        providesTags: (result, error, { matterId }) => [
          { type: 'notifications', id: `conversation-${matterId}` },
        ],
      }),
    }),
    overrideExisting: true, // Override the generated endpoints
  });

export { injectedRtkApi as enhancedApi };

export const {
  useNotificationListQuery,
  useNotificationCreateMutation,
  useNotificationMarkReadMutation,
  useNotificationMarkAllReadMutation,
  useNotificationUnreadCountQuery,
  useNotificationUnreadCountsQuery,
  useNotificationContextQuery,
  useClientConversationsListQuery,
  useClientConversationDetailQuery,
} = injectedRtkApi;

// Re-export MarkReadSchema from generated service
export type { MarkReadSchema } from './generated/notificationsService';
